import { track } from "./umami";
import { siteConfig } from "./config";
import type { BillingInterval, PlanCode } from "./plans";

export interface PublicPlan {
  name?: string;
  monthlyPrice?: number;
  annualPrice?: number;
  features?: string[];
  limits?: {
    maxUsers?: number | null;
    maxActiveJobs?: number | null;
    logRetentionDays?: number | null;
  };
  maxOfflineDays?: number;
  checkout?: {
    monthlyUrl?: string;
    annualUrl?: string;
  };
}

export interface PublicConfig {
  product?: string;
  plans?: Partial<Record<PlanCode, PublicPlan>>;
  billingPortalUrl?: string;
  enterpriseContactUrl?: string;
  downloadUrl?: string;
  supportEmail?: string;
}

export interface LicenseLookupResult {
  status: "ready" | "pending" | "failed";
  licenseKey?: string;
  licenseId?: string;
  customerEmail?: string;
  planName?: string;
  planCode?: string;
  expiresAt?: string;
  downloadUrl?: string;
  message?: string;
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  try {
    const response = await fetch(siteConfig.publicConfigUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      cache: "no-store"
    });
    if (!response.ok) {
      track("service_error", { service: "public-config", result: "http-error" });
      return {};
    }
    return (await response.json()) as PublicConfig;
  } catch {
    track("service_error", { service: "public-config", result: "unavailable" });
    return {};
  }
}

export function checkoutUrl(config: PublicConfig, plan: PlanCode, interval: BillingInterval): string {
  const publicPlan = config.plans?.[plan];
  const configured =
    interval === "annual" ? publicPlan?.checkout?.annualUrl : publicPlan?.checkout?.monthlyUrl;
  const raw = configured || `${siteConfig.apiBaseUrl}/fileagent/checkout?plan=${plan}&interval=${interval}`;
  return withQuery(raw, "redirect", "true");
}

export function enterpriseContactUrl(config: PublicConfig): string {
  return config.enterpriseContactUrl || `mailto:${siteConfig.supportEmail}?subject=FileAgent%20Enterprise`;
}

export function configuredDownloadUrl(config: PublicConfig): string {
  return config.downloadUrl || siteConfig.downloadUrl;
}

export interface ContactSalesPayload {
  name: string;
  email: string;
  company: string;
  companySize: string;
  users: string;
  jobs: string;
  systems: string[];
  security: string[];
  timeline: string;
  message: string;
  phone?: string;
  /** Honeypot — must stay empty for real users. */
  companyWebsite?: string;
}

export interface ContactSalesResult {
  ok: boolean;
  message?: string;
}

/**
 * Submit the Enterprise "Request Pricing" form to the licensing bridge, which
 * emails support@fileagent.app via Resend. No secrets are involved client-side —
 * the recipient and sender are fixed server-side.
 */
export async function submitContactSales(payload: ContactSalesPayload): Promise<ContactSalesResult> {
  const url = `${siteConfig.apiBaseUrl}/fileagent/contact-sales`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      return { ok: true };
    }

    let message: string | undefined;
    try {
      const data = await response.json();
      message = data?.error?.message;
    } catch {
      /* ignore parse errors */
    }
    return { ok: false, message };
  } catch {
    return { ok: false };
  }
}

export type ResendLicenseResult =
  | { status: "ok" }
  | { status: "not_found"; message?: string }
  | { status: "error"; message?: string };

/**
 * Ask the licensing bridge to resend the activation/license email for a given
 * Customer ID (the Stripe customer id shown on the FileAgent System Status page).
 * The bridge maps the Customer ID to the billing email server-side — no email
 * address or license key is ever sent to or returned from the browser.
 */
export async function resendLicense(customerId: string): Promise<ResendLicenseResult> {
  const url = `${siteConfig.apiBaseUrl}/fileagent/license/resend`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
      body: JSON.stringify({ customerId }),
    });
    if (response.ok) {
      return { status: "ok" };
    }
    let code: string | undefined;
    let message: string | undefined;
    try {
      const data = await response.json();
      code = data?.error?.code;
      message = data?.error?.message;
    } catch {
      /* ignore */
    }
    if (response.status === 404 || code === "customer_not_found") {
      return { status: "not_found", message };
    }

    return { status: "error", message };
  } catch {
    return { status: "error" };
  }
}

export async function fetchLicenseForSession(sessionId: string): Promise<LicenseLookupResult> {
  const url = `${siteConfig.apiBaseUrl}${siteConfig.licenseLookupPath}?session_id=${encodeURIComponent(sessionId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit",
    cache: "no-store"
  });

  if (response.status === 404) {
    return { status: "pending", message: "Your license is still being prepared." };
  }
  if (!response.ok) {
    return { status: "failed", message: "The licensing service could not load this purchase." };
  }

  return normalizeLicenseResult(await response.json());
}

function normalizeLicenseResult(raw: unknown): LicenseLookupResult {
  if (!raw || typeof raw !== "object") {
    return { status: "failed", message: "Unexpected licensing response." };
  }
  const record = raw as Record<string, unknown>;
  const rawStatus = typeof record.status === "string" ? record.status.toLowerCase() : "";
  const status: LicenseLookupResult["status"] =
    rawStatus === "ready" || rawStatus === "active" || rawStatus === "success"
      ? "ready"
      : rawStatus === "failed" || rawStatus === "error"
        ? "failed"
        : typeof record.licenseKey === "string" || typeof record.license_key === "string"
          ? "ready"
          : "pending";

  const result: LicenseLookupResult = { status };
  for (const key of ["licenseKey", "licenseId", "customerEmail", "planName", "planCode", "expiresAt", "downloadUrl", "message"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      result[key as keyof LicenseLookupResult] = value.trim() as never;
    }
  }
  if (!result.licenseKey && typeof record.license_key === "string") {
    result.licenseKey = record.license_key;
  }
  if (result.status === "ready" && !result.licenseKey) {
    return { status: "pending", message: "Your license is still being finalized." };
  }
  return result;
}

function withQuery(rawUrl: string, key: string, value: string): string {
  const url = new URL(rawUrl, window.location.origin);
  url.searchParams.set(key, value);
  return url.toString();
}
